import { useState } from "react";
import { Home as HomeIcon, Building2, Sun, LayoutGrid, PhoneCall } from "lucide-react";
import { IMG } from "@/lib/images";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/Shared";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";

const filters = [
  { key: "residential", label: "RESIDENTIAL", icon: HomeIcon },
  { key: "commercial", label: "COMMERCIAL", icon: Building2 },
  { key: "utility", label: "UTILITY SOLAR FARMS", icon: Sun },
  { key: "all", label: "ALL PROJECTS", icon: LayoutGrid },
];

const residential = [IMG.res1, IMG.res3, IMG.res2, IMG.res4];
const commercial = [IMG.comm1, IMG.comm2, IMG.comm3, IMG.comm2];
const utility = [IMG.farm1, IMG.farm2, IMG.farm3, IMG.farmSunset];
const beforeAfter = [
  { img: IMG.res3, label: "RESIDENTIAL" },
  { img: IMG.comm2, label: "COMMERCIAL" },
  { img: IMG.farm4, label: "SOLAR FARM" },
  { img: IMG.farm2, label: "SOLAR FARM" },
];
const teamAtWork = [
  { img: IMG.truck, cap: "COMPANY TRUCK" },
  { img: IMG.equipment, cap: "CLEANING EQUIPMENT" },
  { img: IMG.panelClean, cap: "CLEANED ARRAY" },
  { img: IMG.farm3, cap: "SOLAR FARM ACCESS" },
];

const Grid = ({ images }) => (
  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
    {images.map((src, i) => (
      <Reveal key={i} delay={(i % 4) * 0.06}>
        <div className="group overflow-hidden rounded-xl shadow-[0_10px_30px_-16px_rgba(10,35,66,0.4)]">
          <img src={src} alt="Solar project" className="h-40 w-full object-cover transition-transform duration-500 group-hover:scale-110 md:h-44" />
        </div>
      </Reveal>
    ))}
  </div>
);

const GalleryHeading = ({ children }) => (
  <div className="mb-5 flex items-center gap-4">
    <h2 className="font-display text-xl font-extrabold text-[#1E5EDB]">{children}</h2>
    <span className="h-[2px] w-16 rounded-full bg-[#1E5EDB]/40" />
  </div>
);

export default function Gallery() {
  const [active, setActive] = useState("all");
  const show = (key) => active === "all" || active === key;

  return (
    <div data-testid="gallery-page">
      <PageHero
        title="OUR GALLERY"
        text="Take a look at the projects we've completed for homes, businesses, and solar farms across Texas."
      />

      <section className="mx-auto max-w-[1200px] px-5 lg:px-8 py-14">
        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          {filters.map((f) => {
            const isActive = active === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setActive(f.key)}
                data-testid={`gallery-filter-${f.key}`}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 font-display text-[13px] font-bold tracking-wide transition-colors ${
                  isActive ? "bg-[#1E5EDB] text-white" : "border border-[#E4EBF3] bg-white text-[#0A2342] hover:border-[#1E5EDB]"
                }`}
              >
                <f.icon size={17} className={isActive ? "text-white" : "text-[#5B6B7F]"} />
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="mt-12 space-y-14">
          {show("residential") && (
            <div><GalleryHeading>RESIDENTIAL HOMES</GalleryHeading><Grid images={residential} /></div>
          )}
          {show("commercial") && (
            <div><GalleryHeading>COMMERCIAL PROPERTIES</GalleryHeading><Grid images={commercial} /></div>
          )}
          {show("utility") && (
            <div><GalleryHeading>UTILITY-SCALE SOLAR FARMS</GalleryHeading><Grid images={utility} /></div>
          )}

          {active === "all" && (
            <>
              <div>
                <GalleryHeading>BEFORE &amp; AFTER RESULTS</GalleryHeading>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {beforeAfter.map((b, i) => (
                    <Reveal key={i} delay={(i % 4) * 0.06}>
                      <BeforeAfterSlider image={b.img} label={b.label} testid={`gallery-ba-${i}`} className="h-40 md:h-44" />
                    </Reveal>
                  ))}
                </div>
              </div>

              <div>
                <GalleryHeading>OUR TEAM AT WORK</GalleryHeading>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  {teamAtWork.map((t, i) => (
                    <Reveal key={i} delay={(i % 4) * 0.06}>
                      <div className="group relative overflow-hidden rounded-xl shadow-[0_10px_30px_-16px_rgba(10,35,66,0.4)]">
                        <img src={t.img} alt={t.cap} className="h-40 w-full object-cover transition-transform duration-500 group-hover:scale-110 md:h-44" />
                        <span className="absolute bottom-2 right-2 rounded bg-black/55 px-2 py-0.5 font-display text-[10px] font-bold tracking-wide text-white">{t.cap}</span>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <CtaBand
        icon={PhoneCall}
        title="Have a project you'd like cleaned?"
        text="Let us help you protect your investment and maximize your solar system's performance."
        buttonLabel="GET A FREE QUOTE"
      />
    </div>
  );
}
